import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface SimpleBarRow {
  bucketLabel: string;
  value: number;
}

interface StatisticsVolunteerSimpleBarChartProps {
  title: string;
  data: SimpleBarRow[];
  isLoading?: boolean;
  valueLabel?: string;
}

export const StatisticsVolunteerSimpleBarChart: FC<
  StatisticsVolunteerSimpleBarChartProps
> = ({ title, data, isLoading, valueLabel = "מתנדבים" }) => {
  const maxValue = data.length ? Math.max(...data.map((r) => r.value)) : 0;
  const yAxisWidth = maxValue >= 1000 ? 56 : maxValue >= 100 ? 48 : 40;
  const denseAxis = data.length > 6;

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", minWidth: 0, py: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          טוען…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 600,
          color: "text.primary",
          textAlign: "center",
          mb: 1,
        }}
      >
        {title}
      </Typography>

      {!data.length ? (
        <Box
          sx={{
            py: 2,
            px: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            אין נתונים
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 12, right: 8, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="4 4" />
              <XAxis
                dataKey="bucketLabel"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={denseAxis ? -35 : 0}
                textAnchor={denseAxis ? "end" : "middle"}
                height={denseAxis ? 72 : 36}
              />
              <YAxis
                allowDecimals={false}
                width={yAxisWidth}
                tickMargin={6}
                tick={{ fontSize: 11 }}
              />
              <RechartsTooltip
                formatter={(v: number) => [v, valueLabel]}
                contentStyle={{ direction: "rtl" }}
              />
              <Bar dataKey="value" fill="#05204c" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

